import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Star, Quote, MapPin, DollarSign, Play, Video, CheckCircle, Award } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from './ui/dialog';

// Video testimonials data - All male names with pre-extracted thumbnails
const videoTestimonials = [
  {
    id: 1,
    name: 'Mohammed Al Ali',
    nameAr: 'محمد العلي',
    location: 'United Arab Emirates',
    locationAr: 'الإمارات العربية المتحدة',
    amount: '$847,500',
    case: 'Forex Trading Fraud',
    caseAr: 'احتيال تداول فوركس',
    videoUrl: 'https://customer-assets.emergentagent.com/job_f2c3b0e3-3523-4eb0-a096-8dd931733a9f/artifacts/bnmgdojw_d5c01a99-1630-4d5b-b148-038e82216bbc.mov',
    thumbnail: '/thumbnails/thumb-1.jpg'
  },
  {
    id: 2,
    name: 'Khalid Al Rashid',
    nameAr: 'خالد الراشد',
    location: 'Saudi Arabia',
    locationAr: 'المملكة العربية السعودية',
    amount: '$625,000',
    case: 'Binary Options Scam',
    caseAr: 'احتيال خيارات ثنائية',
    videoUrl: 'https://customer-assets.emergentagent.com/job_f2c3b0e3-3523-4eb0-a096-8dd931733a9f/artifacts/0vsybuad_fa3f0730-9f93-4929-bbd8-a642e6b92e8e.mov',
    thumbnail: '/thumbnails/thumb-2.jpg'
  },
  {
    id: 3,
    name: 'Ahmed Al Khatib',
    nameAr: 'أحمد الخطيب',
    location: 'Syria',
    locationAr: 'سوريا',
    amount: '$71,000',
    case: 'Crypto Investment Fraud',
    caseAr: 'احتيال استثمار العملات المشفرة',
    videoUrl: 'https://customer-assets.emergentagent.com/job_f2c3b0e3-3523-4eb0-a096-8dd931733a9f/artifacts/5l9vhrqk_de795509-e86b-4e9b-bd72-0f63faaa4f14.mov',
    thumbnail: '/thumbnails/thumb-3.jpg'
  },
  {
    id: 4,
    name: 'Omar Hassan',
    nameAr: 'عمر حسن',
    location: 'Qatar',
    locationAr: 'قطر',
    amount: '$395,000',
    case: 'Investment Platform Scam',
    caseAr: 'احتيال منصة استثمار',
    videoUrl: 'https://customer-assets.emergentagent.com/job_f2c3b0e3-3523-4eb0-a096-8dd931733a9f/artifacts/q1obq1xq_039d3248-27ce-4e39-8895-7dd2713a2ab1.mov',
    thumbnail: '/thumbnails/thumb-4.jpg'
  },
  {
    id: 5,
    name: 'Faisal Rahman',
    nameAr: 'فيصل رحمان',
    location: 'Bahrain',
    locationAr: 'البحرين',
    amount: '$982,000',
    case: 'Trading Bot Fraud',
    caseAr: 'احتيال روبوت التداول',
    videoUrl: 'https://customer-assets.emergentagent.com/job_f2c3b0e3-3523-4eb0-a096-8dd931733a9f/artifacts/hzzpimd3_c54b5d2d-2c6e-4164-b888-2e5a0e7e3d52.mov',
    thumbnail: '/thumbnails/thumb-5.jpg'
  }
];

// Component to show video thumbnail using pre-extracted static image
const VideoThumbnailCard = ({ thumbnail, className, children, isAbsolute = false }) => {
  const containerStyle = isAbsolute ? { position: 'absolute', inset: 0 } : { position: 'relative' };
  
  return (
    <div className={className} style={containerStyle}>
      {/* Static thumbnail image extracted from video */}
      <img
        src={thumbnail}
        alt="Video thumbnail"
        className="w-full h-full object-cover"
        loading="eager"
        style={{ position: 'absolute', inset: 0 }}
      />
      {children}
    </div>
  );
};

// Text testimonials with realistic Arab photos and diverse stories
const textTestimonials = [
  {
    id: 1,
    name: 'Youssef Al Mansoori',
    nameAr: 'يوسف المنصوري',
    location: 'Dubai, UAE',
    locationAr: 'دبي، الإمارات',
    amount: '$156,000',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1656399910161-8efafd973a29?w=200&h=200&fit=crop',
    text: 'I lost my life savings to a fake forex broker. Dr. Qasim and his team recovered every dollar within 6 months. Their expertise in international law is unmatched.',
    textAr: 'خسرت مدخراتي كلها لوسيط فوركس محتال. الدكتور قاسم وفريقه استردوا كل دولار خلال 6 أشهر. خبرتهم في القانون الدولي لا مثيل لها.'
  },
  {
    id: 2,
    name: 'Abdulrahman Al Otaibi',
    nameAr: 'عبدالرحمن العتيبي',
    location: 'Riyadh, Saudi Arabia',
    locationAr: 'الرياض، السعودية',
    amount: '$423,000',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1667839419946-f6c6c2bdf332?w=200&h=200&fit=crop',
    text: 'A cryptocurrency platform disappeared with my investment. I thought all was lost until I found Dr. Qasim. His legal strategy was brilliant - we won in court!',
    textAr: 'اختفت منصة عملات مشفرة بأموالي. ظننت أن كل شيء ضاع حتى وجدت د. قاسم. استراتيجيته القانونية كانت عبقرية - فزنا في المحكمة!'
  },
  {
    id: 3,
    name: 'Hassan Al Yemeni',
    nameAr: 'حسن اليمني',
    location: 'Sana\'a, Yemen',
    locationAr: 'صنعاء، اليمن',
    amount: '$89,000',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1765366574945-e2f1b4b1a5b3?w=200&h=200&fit=crop',
    text: 'Despite my difficult circumstances, Dr. Qasim took my case and fought for me. He recovered funds I never thought I would see again. A true professional.',
    textAr: 'رغم ظروفي الصعبة، قبل د. قاسم قضيتي وكافح من أجلي. استرد أموالاً لم أكن أتوقع رؤيتها مرة أخرى. محترف حقيقي.'
  },
  {
    id: 4,
    name: 'Mahmoud El-Sayed',
    nameAr: 'محمود السيد',
    location: 'Cairo, Egypt',
    locationAr: 'القاهرة، مصر',
    amount: '$234,000',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1645106281521-86da01d1031d?w=200&h=200&fit=crop',
    text: 'I was scammed by a binary options company. The British courts ruled in my favor thanks to Dr. Qasim\'s meticulous preparation. Highly recommended!',
    textAr: 'تعرضت للاحتيال من شركة خيارات ثنائية. المحاكم البريطانية حكمت لصالحي بفضل إعداد د. قاسم الدقيق. أنصح به بشدة!'
  }
];

const VideoPlayerDialog = ({ testimonial, language, direction, t, children }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-[#0A1628] border-[#C9A55A]/30" aria-describedby={`video-desc-${testimonial.id}`}>
        <DialogTitle className="sr-only">
          {language === 'ar' ? `شهادة فيديو من ${testimonial.nameAr}` : `Video testimonial from ${testimonial.name}`}
        </DialogTitle>
        <DialogDescription id={`video-desc-${testimonial.id}`} className="sr-only">
          Video testimonial showing client experience with fund recovery
        </DialogDescription>
        
        {/* Video Player */}
        <div className="relative bg-black">
          <video
            className="w-full max-h-[70vh]"
            controls
            autoPlay
            playsInline
          >
            <source src={testimonial.videoUrl} type="video/quicktime" />
            <source src={testimonial.videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        
        {/* Client Info Footer */}
        <div className="p-6 bg-gradient-to-r from-[#0A1628] to-[#1a2942]">
          <div className={`flex items-center justify-between ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-4 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
              {/* Avatar */}
              <div className="w-14 h-14 bg-gradient-to-br from-[#C9A55A] to-[#D4AF37] rounded-full flex items-center justify-center text-[#0A1628] font-bold text-xl">
                {(language === 'ar' ? testimonial.nameAr : testimonial.name).charAt(0)}
              </div>
              <div className={direction === 'rtl' ? 'text-right' : ''}>
                <p className="font-bold text-white text-lg">
                  {language === 'ar' ? testimonial.nameAr : testimonial.name}
                </p>
                <div className={`flex items-center gap-2 text-gray-400 text-sm ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                  <MapPin className="w-4 h-4" />
                  {language === 'ar' ? testimonial.locationAr : testimonial.location}
                </div>
              </div>
            </div>
            
            <div className={`text-right ${direction === 'rtl' ? 'text-left' : ''}`}>
              <p className="text-[#C9A55A] text-2xl font-bold">{testimonial.amount}</p>
              <p className="text-gray-400 text-sm">{t.ui.recovered}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const VideoCard = ({ testimonial, language, direction, t, index }) => {
  return (
    <VideoPlayerDialog testimonial={testimonial} language={language} direction={direction} t={t}>
      <div 
        className="group relative cursor-pointer"
        data-testid={`video-testimonial-${testimonial.id}`}
        style={{ animationDelay: `${index * 0.1}s` }}
      >
        {/* Main Card */}
        <div className="relative bg-gradient-to-br from-[#0A1628] via-[#0f2444] to-[#0A1628] rounded-2xl overflow-hidden border border-[#C9A55A]/20 hover:border-[#C9A55A]/50 transition-all duration-500 hover:shadow-[0_0_40px_rgba(201,165,90,0.15)] hover:-translate-y-2">
          
          {/* Video Thumbnail Area - Shows pre-extracted first frame of video */}
          <VideoThumbnailCard thumbnail={testimonial.thumbnail} className="h-64 overflow-hidden bg-[#0A1628]">
            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/40 to-transparent" />
            
            {/* Play Button Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative z-10 transform group-hover:scale-110 transition-transform duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-[#C9A55A] to-[#D4AF37] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(201,165,90,0.5)] opacity-90 group-hover:opacity-100">
                  <Play className="w-9 h-9 text-[#0A1628] ml-1" fill="#0A1628" />
                </div>
              </div>
            </div>
            
            {/* Top Badges */}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-20">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/90 backdrop-blur-sm rounded-full">
                <CheckCircle className="w-4 h-4 text-white" />
                <span className="text-white text-xs font-bold">
                  {t.ui.verified}
                </span>
              </div>
              <div className="px-4 py-1.5 bg-[#C9A55A] rounded-full shadow-lg">
                <span className="text-[#0A1628] text-sm font-bold">{testimonial.amount}</span>
              </div>
            </div>
            
            {/* Video Icon Badge */}
            <div className="absolute bottom-4 right-4 z-20">
              <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                <Video className="w-5 h-5 text-white" />
              </div>
            </div>
          </VideoThumbnailCard>
          
          {/* Info Section */}
          <div className="p-5 border-t border-[#C9A55A]/10">
            {/* Client Info */}
            <div className={`flex items-center gap-4 mb-4 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
              {/* Avatar - First letter */}
              <div className="w-12 h-12 bg-gradient-to-br from-[#C9A55A]/20 to-[#C9A55A]/10 rounded-full flex items-center justify-center text-[#C9A55A] font-bold text-lg border border-[#C9A55A]/30">
                {(language === 'ar' ? testimonial.nameAr : testimonial.name).charAt(0)}
              </div>
              <div className={direction === 'rtl' ? 'text-right' : ''}>
                <p className="font-bold text-white text-lg">
                  {language === 'ar' ? testimonial.nameAr : testimonial.name}
                </p>
                <div className={`flex items-center gap-1.5 text-gray-400 text-sm ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                  <MapPin className="w-3.5 h-3.5" />
                  {language === 'ar' ? testimonial.locationAr : testimonial.location}
                </div>
              </div>
            </div>
            
            {/* Case Type */}
            <div className={`flex items-center gap-2 mb-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <Award className="w-4 h-4 text-[#C9A55A]" />
              <span className="text-gray-300 text-sm">
                {language === 'ar' ? testimonial.caseAr : testimonial.case}
              </span>
            </div>
            
            {/* Rating */}
            <div className={`flex items-center gap-1 ${direction === 'rtl' ? 'justify-end' : ''}`}>
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[#C9A55A] text-[#C9A55A]" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </VideoPlayerDialog>
  );
};

const Testimonials = () => {
  const { t, language, direction } = useLanguage();

  return (
    <section id="testimonials" className="py-24 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A55A]/30 to-transparent" />
      <div className="absolute top-40 left-0 w-96 h-96 bg-[#C9A55A]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-40 right-0 w-96 h-96 bg-[#0A1628]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-6 relative">
        
        {/* Section Header */}
        <div className={`text-center mb-16 ${direction === 'rtl' ? 'rtl' : ''}`}>
          <div className="inline-flex items-center gap-3 px-6 py-2.5 bg-[#0A1628] border border-[#C9A55A]/30 rounded-full mb-6">
            <Video className="w-5 h-5 text-[#C9A55A]" />
            <span className="text-sm font-bold text-[#C9A55A] uppercase tracking-wider">
              {t.ui.videoTestimonials}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            {t.testimonials.title}
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-[#C9A55A] to-transparent mx-auto mb-6" />
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.testimonials.subtitle}
          </p>
        </div>

        {/* Video Testimonials Grid - Premium Layout */}
        <div className="mb-20">
          {/* Featured Video (First one larger) */}
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            {/* Large Featured Card */}
            <div className="lg:col-span-2">
              <VideoPlayerDialog testimonial={videoTestimonials[0]} language={language} direction={direction} t={t}>
                <div 
                  className="group relative cursor-pointer h-full"
                  data-testid={`video-testimonial-featured`}
                >
                  <div className="relative rounded-3xl overflow-hidden border border-[#C9A55A]/20 hover:border-[#C9A55A]/50 transition-all duration-500 hover:shadow-[0_0_60px_rgba(201,165,90,0.2)] h-full min-h-[400px]">
                    
                    {/* Video as Background - shows pre-extracted first frame */}
                    <VideoThumbnailCard thumbnail={videoTestimonials[0].thumbnail} className="" isAbsolute={true}>
                      {/* Dark Overlay - Lighter to show thumbnail */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/30 to-transparent z-10" />
                    </VideoThumbnailCard>
                    
                    {/* Content */}
                    <div className="absolute inset-0 flex items-center justify-center z-20">
                      {/* Play Button */}
                      <div className="relative z-10 transform group-hover:scale-110 transition-transform duration-300">
                        <div className="w-28 h-28 bg-gradient-to-br from-[#C9A55A] to-[#D4AF37] rounded-full flex items-center justify-center shadow-[0_0_50px_rgba(201,165,90,0.6)]">
                          <Play className="w-14 h-14 text-[#0A1628] ml-2" fill="#0A1628" />
                        </div>
                        <p className="text-white text-lg mt-6 text-center font-medium drop-shadow-lg">
                          {t.ui.watchFullStory}
                        </p>
                      </div>
                    </div>
                    
                    {/* Top Badges */}
                    <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-20">
                      <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/90 backdrop-blur-sm rounded-full">
                        <CheckCircle className="w-5 h-5 text-white" />
                        <span className="text-white text-sm font-bold">
                          {t.ui.verifiedSuccessStory}
                        </span>
                      </div>
                      <div className="px-5 py-2 bg-[#C9A55A] rounded-full shadow-lg">
                        <span className="text-[#0A1628] text-lg font-bold">{videoTestimonials[0].amount}</span>
                      </div>
                    </div>
                    
                    {/* Bottom Info */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#0A1628] to-transparent z-20">
                      <div className={`flex items-center gap-4 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                        <div className="w-14 h-14 bg-gradient-to-br from-[#C9A55A] to-[#D4AF37] rounded-full flex items-center justify-center text-[#0A1628] font-bold text-xl border-2 border-[#C9A55A]">
                          {(language === 'ar' ? videoTestimonials[0].nameAr : videoTestimonials[0].name).charAt(0)}
                        </div>
                        <div className={direction === 'rtl' ? 'text-right' : ''}>
                          <p className="font-bold text-white text-xl">
                            {language === 'ar' ? videoTestimonials[0].nameAr : videoTestimonials[0].name}
                          </p>
                          <p className="text-gray-400">
                            {language === 'ar' ? videoTestimonials[0].locationAr : videoTestimonials[0].location}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </VideoPlayerDialog>
            </div>
            
            {/* Two Stacked Cards */}
            <div className="flex flex-col gap-6">
              {videoTestimonials.slice(1, 3).map((testimonial, idx) => (
                <VideoCard 
                  key={testimonial.id} 
                  testimonial={testimonial} 
                  language={language}
                  direction={direction}
                  t={t}
                  index={idx}
                />
              ))}
            </div>
          </div>
          
          {/* Bottom Row - Two Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {videoTestimonials.slice(3, 5).map((testimonial, idx) => (
              <VideoCard 
                key={testimonial.id} 
                testimonial={testimonial} 
                language={language}
                direction={direction}
                t={t}
                index={idx + 2}
              />
            ))}
          </div>
        </div>

        {/* Text Testimonials with Photos */}
        <div className="mb-16">
          <h3 className={`text-2xl font-bold text-[#0A1628] mb-8 ${direction === 'rtl' ? 'text-right' : ''}`} style={{ fontFamily: 'Georgia, serif' }}>
            {t.ui.moreClientReviews}
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {textTestimonials.map((testimonial, idx) => (
              <div 
                key={testimonial.id}
                className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-[#C9A55A]/30"
                data-testid={`testimonial-${idx}`}
              >
                {/* Quote Icon */}
                <div className="absolute -top-4 -left-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#C9A55A] to-[#D4AF37] rounded-full flex items-center justify-center shadow-lg">
                    <Quote className="w-5 h-5 text-white" />
                  </div>
                </div>
                
                {/* Rating */}
                <div className={`flex gap-1 mb-4 ${direction === 'rtl' ? 'justify-end' : ''}`}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#C9A55A] text-[#C9A55A]" />
                  ))}
                </div>
                
                {/* Testimonial Text */}
                <p className={`text-gray-700 leading-relaxed mb-6 text-lg ${direction === 'rtl' ? 'text-right' : ''}`}>
                  "{language === 'ar' ? testimonial.textAr : testimonial.text}"
                </p>
                
                {/* Client Info with Photo */}
                <div className={`flex items-center justify-between pt-6 border-t border-gray-100 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-4 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                    {/* Real Photo */}
                    <img 
                      src={testimonial.image} 
                      alt={language === 'ar' ? testimonial.nameAr : testimonial.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-[#C9A55A]/30"
                    />
                    <div className={direction === 'rtl' ? 'text-right' : ''}>
                      <p className="font-bold text-[#0A1628] text-lg">
                        {language === 'ar' ? testimonial.nameAr : testimonial.name}
                      </p>
                      <div className={`flex items-center gap-2 text-gray-500 text-sm ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                        <MapPin className="w-4 h-4" />
                        {language === 'ar' ? testimonial.locationAr : testimonial.location}
                      </div>
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                    <span className="font-bold text-emerald-600">{testimonial.amount}</span>
                  </div>
                </div>
                
                {/* Verified Badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                    {direction === 'rtl' ? 'موثق' : 'Verified'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '4.9/5', label: direction === 'rtl' ? 'تقييم العملاء' : 'Client Rating', icon: Star },
            { value: '98%', label: direction === 'rtl' ? 'رضا العملاء' : 'Satisfaction', icon: CheckCircle },
            { value: '180+', label: direction === 'rtl' ? 'عميل سعيد' : 'Happy Clients', icon: Award },
            { value: '100%', label: direction === 'rtl' ? 'استشارة مجانية' : 'Free Consult', icon: Video }
          ].map((stat, idx) => (
            <div key={idx} className="text-center p-6 bg-gradient-to-br from-[#0A1628] to-[#1a2942] rounded-2xl border border-[#C9A55A]/20">
              <stat.icon className="w-8 h-8 text-[#C9A55A] mx-auto mb-3" />
              <p className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Georgia, serif' }}>
                {stat.value}
              </p>
              <p className="text-sm text-gray-400">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
